import { Fragment, MouseEvent, useMemo } from 'react';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { EMPTY_ROW_KEY, STATUS_ROW_KEY } from '../constants';
import { Column } from '../types';
import { DataGridTableCell } from './DataGridTableCell';
import { TableStatusRow } from './TableStatusRow';
import {
  groupVisibleRows,
  renderSectionLabel,
} from '../../AccreditationForm/helpers/grouping-helpers';
import {
  CATEGORY_ORDER,
  GROUPS,
} from '../../AccreditationForm/constants/grouping-constants';
import { MaxApplicationsPerRole } from '@r-and-a-shared-ui/common-forms-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faForkKnife } from '@fortawesome/pro-regular-svg-icons';

import styles from '../DataGridTable.module.scss';

type DataGridTableBodyProps = {
  columns: Column[];
  visibleRows: any[];
  loading: boolean;
  hasFetched: boolean;
  isManagersTable: boolean;
  isAccreditationGridTable?: boolean;
  maxNumberOfApplicationsPerRole?: MaxApplicationsPerRole[];
  onRowClick: (event: MouseEvent<unknown>, id: string) => void;
};

const TABLE_LEGEND_LABEL = 'Nominated for Premium Dining Guest';

export const DataGridTableBody = ({
  columns,
  visibleRows,
  loading,
  hasFetched,
  isManagersTable,
  onRowClick,
  isAccreditationGridTable = false,
  maxNumberOfApplicationsPerRole,
}: DataGridTableBodyProps) => {
  const renderDataRow = (row: any) => (
    <TableRow
      key={row.id}
      hover
      onClick={event => onRowClick(event, row.id)}
      sx={isManagersTable ? { cursor: 'pointer' } : undefined}
    >
      {columns?.map(col => (
        <DataGridTableCell key={col.id} row={row} col={col} />
      ))}
    </TableRow>
  );

  const groupedRows = useMemo(() => {
    if (!isAccreditationGridTable) {
      return null;
    }

    return groupVisibleRows(visibleRows);
  }, [isAccreditationGridTable, visibleRows]);

  const renderAccreditFormSections = () => {
    if (loading || !hasFetched || !groupedRows) {
      return (
        <TableStatusRow
          colSpan={columns?.length ?? 1}
          loading={loading}
          hasFetched={hasFetched}
        />
      );
    }

    return CATEGORY_ORDER.map(group => {
      const rows = groupedRows?.[group] ?? [];
      const labelNode = renderSectionLabel(
        group,
        rows,
        maxNumberOfApplicationsPerRole,
      );

      return (
        <Fragment key={group}>
          {labelNode && (
            <TableRow className={styles.sectionLabelRow}>
              <TableCell
                colSpan={columns?.length}
                className={styles.sectionLabelCell}
              >
                {labelNode}
              </TableCell>
            </TableRow>
          )}
          {rows.length > 0 ? (
            rows.map(renderDataRow)
          ) : group === GROUPS.children ? (
            <TableStatusRow
              colSpan={columns?.length ?? 1}
              loading={loading}
              hasFetched={hasFetched}
            />
          ) : null}
        </Fragment>
      );
    });
  };

  return (
    <TableBody className={styles.tbody}>
      {isAccreditationGridTable
        ? renderAccreditFormSections()
        : visibleRows.map(renderDataRow)}

      {!isAccreditationGridTable && visibleRows.length === 0 && (
        <TableStatusRow
          colSpan={columns?.length ?? 1}
          loading={loading}
          hasFetched={hasFetched}
        />
      )}

      {isAccreditationGridTable && visibleRows.length > 0 && (
        <TableRow className={styles.tableLegendRow}>
          <TableCell colSpan={columns?.length}>
            <FontAwesomeIcon
              icon={faForkKnife}
              className={styles.forkKnifeIcon}
            />
            <span> {TABLE_LEGEND_LABEL}</span>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
};
